import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import { ApiError } from '../api/client'
import { ComfortProvider } from '../comfort/ComfortProvider'
import { StudentComfortSync } from '../comfort/StudentComfortSync'
import { ToastProvider } from '../design/components'
import { I18nProvider, type Lang } from '../i18n'
import { SessionProvider } from '../session/SessionProvider'

/** Statuses where retrying cannot help: the answer will be the same. */
const FINAL_STATUSES = new Set([400, 401, 403, 404, 409, 423])

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        retry: (failureCount, error) =>
          !(error instanceof ApiError && FINAL_STATUSES.has(error.status)) && failureCount < 2,
      },
      mutations: { retry: false },
    },
  })
}

export interface AppProvidersProps {
  queryClient: QueryClient
  /** Tests pin the language; the app detects it. */
  lang?: Lang
  children: ReactNode
}

/** Everything a screen can rely on: data, language, session, comfort and toasts. */
export function AppProviders({ queryClient, lang, children }: AppProvidersProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <I18nProvider initialLang={lang}>
        <SessionProvider>
          <ComfortProvider>
            <ToastProvider>
              <StudentComfortSync />
              {children}
            </ToastProvider>
          </ComfortProvider>
        </SessionProvider>
      </I18nProvider>
    </QueryClientProvider>
  )
}
